import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { JobOffer, Candidate } from '../../models/job';
import { toast } from 'sonner';
import { getCandidateAssessmentSummary } from '../../services/job.service';
import { ChevronLeft, ChevronRight, SquareArrowOutUpRight, X, AlertCircle } from 'lucide-react';
import {
  getInterviewSelfieDetails,
  getInterviewSelfieVideoBlob
} from '../../services/interview.service';
import { useStreamableVideo } from '../../hooks/useStreamableVideo';
import { useAuth0 } from '@auth0/auth0-react';
import { useCandidateEmail } from '../../hooks/useCandidateEmail';
import { isValidEmail } from '../../utils/candidate-email';
import { CandidateInfo } from './CandidateInfo';
import { CandidateAssessmentSummary, ExamConfig } from '../../types/job.types';
import { getInterviewLink } from '../../utils/config';
import { getEnhancedDataValue, parseEnhancedData } from '../../hooks/useEnhancedData';
import { Tag } from '../ui/atoms/Tag';
import { StatusPill } from '../ui/atoms/StatusPill';
import { EmailField } from '../ui/molecules';
import { SuspiciousActivityBadge } from '../ui/SuspiciousActivityBadge';
import { getScoreIntent, getMatchScoreIntent } from '../../utils/tag.utils';
import { getCandidateStatusVariant } from '../ui/atoms/StatusPill/statusPill.utils';
import { hasCandidateFooterActions } from '../../utils/candidate-actions';
import { useJobPipeline } from '../../hooks/useJobOffers';
import { useStageDisplayLabel, useStageTypeDisplayLabel } from '../../hooks/useStageDisplayLabel';
import {
  findCandidateStage,
  isPendingStageSemantic,
} from '../../utils/pipeline-stages';
import { getCandidateNeighbors } from '../../utils/candidate-navigation';

import { CandidateDownloadDropdown } from '../dashboard/CandidateDownloadDropdown'
import { CandidateActions } from './CandidateActions';
import { Button } from '@/components/ui/atoms/Button';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { ProgressBar } from '../ui/atoms/ProgressBar';

const DEFAULT_EXAM_CONFIG: ExamConfig = {
  technical: {
    coreConceptsStrengths: true,
    coreConceptsWeaknesses: true,
    algorithmic: true,
    cleanCode: true,
    debugging: true,
    tradeoffs: true,
    ai_tools: true,
  },
  softSkills: {
    communication: true,
    teamSpirit: true,
    selfAwareness: true,
    growthMindset: true,
    adaptability: true,
    caseStudy: false,
    lastExperience: true,
  },
};

export interface CandidateSidebarProps {
  candidate?: Candidate;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  job?: JobOffer;
  onStatusUpdate?: () => void;
  examConfig?: ExamConfig;
  requiresReview?: boolean;
  onDeleteCandidate?: (candidateId: string, candidateName: string) => void;
  onExtendInterview?: (
    interviewId: string,
    candidateName: string,
    currentExpiration: string | null
  ) => void;
  // Navigation props
  allCandidates?: Candidate[];
  onNavigateToCandidate?: (candidateId: string) => void;
}

export const CandidateSidebar: React.FC<CandidateSidebarProps> = ({
  candidate,
  isOpen,
  onClose,
  className = '',
  job,
  onStatusUpdate,
  examConfig = DEFAULT_EXAM_CONFIG,
  requiresReview = false,
  onDeleteCandidate,
  onExtendInterview,
  allCandidates = [],
  onNavigateToCandidate
}) => {
  const { t } = useTranslation(['candidates', 'job']);
  const { getAccessTokenSilently } = useAuth0();
  const { updateEmail, isUpdating: isUpdatingEmail } = useCandidateEmail({ jobId: job?.id ?? '' });

  // State for AssessmentModal functionality
  const [assessment, setAssessment] = useState<CandidateAssessmentSummary | null>(null);
  const [isLoadingAssessmentSummary, setIsLoadingAssessmentSummary] = useState<boolean>(true);

  // Inline email editing state
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  // Reset email editing state when candidate changes
  useEffect(() => {
    setIsEditingEmail(false);
    setEmailInput('');
  }, [candidate?.id]);

  // Reset assessment state when candidate changes to prevent stale data showing
  useEffect(() => {
    setAssessment(null);
    setIsLoadingAssessmentSummary(true);
  }, [candidate?.id]);

  // Determine if candidate has been interviewed
  const hasAssessment = Boolean(candidate?.hasAssessmentAvailable);

  const hasInterviewedStatus = hasAssessment;

  // Load detailed assessment summary (from AssessmentModal)
  useEffect(() => {
    const fetchAssessmentSummary = async () => {
      if (hasAssessment && candidate) {
        setIsLoadingAssessmentSummary(true);
        try {
          const accessToken = await getAccessTokenSilently();
          const response = await getCandidateAssessmentSummary(accessToken, candidate.id);

          if (response.error) {
            console.error('Error fetching assessment summary:', response.error);
            toast.error(t('notifications.assessment_load_failed'));
          } else if (response.data) {
            setAssessment(response.data);
          }
        } catch (error) {
          console.error('Error fetching assessment summary:', error);
          toast.error(t('notifications.assessment_load_failed'));
        } finally {
          setIsLoadingAssessmentSummary(false);
        }
      }
    };

    if (hasAssessment && candidate) {
      fetchAssessmentSummary();
    }
  }, [candidate?.id, getAccessTokenSilently, hasAssessment, candidate]);

  // Intro selfie video: streamed from the bucket in staging/production, downloaded
  // through the API otherwise (see hooks/useStreamableVideo).
  const selfieFilenameRef = useRef<string | null>(null);
  const selfieVideo = useStreamableVideo(
    hasAssessment && candidate?.id ? candidate.id : null,
    {
      resolveSource: async () => {
        if (!candidate?.id) return null;
        const accessToken = await getAccessTokenSilently();
        const response = await getInterviewSelfieDetails(accessToken, candidate.id);
        if (response.error) {
          console.error('Error fetching selfie details:', response.error);
          return null;
        }
        if (!response.data?.selfie_filename) {
          return null; // No selfie found
        }
        selfieFilenameRef.current = response.data.selfie_filename;
        const speechStartSeconds = response.data.speech_start_seconds || 0;
        return {
          streamUrl: response.data.selfie_stream_url ?? null,
          // Start 500ms before speech (but not below 0)
          startAt: speechStartSeconds > 0 ? Math.max(0, speechStartSeconds - 0.5) : 0,
        };
      },
      fetchBlob: async () => {
        const selfieFilename = selfieFilenameRef.current;
        if (!candidate?.id || !selfieFilename) {
          throw new Error('Selfie video is not available');
        }
        const accessToken = await getAccessTokenSilently();
        const blobResponse = await getInterviewSelfieVideoBlob(
          accessToken,
          candidate.id,
          selfieFilename
        );
        if (blobResponse.error || !blobResponse.data) {
          console.error('Error fetching selfie video blob:', blobResponse.error);
          throw new Error(blobResponse.error ?? 'Selfie video is not available');
        }
        return blobResponse.data;
      },
    }
  );

  // Handle opening interview link
  const handleOpenInterviewLink = () => {
    if (candidate?.interviewInvitationToken) {
      const interviewUrl = getInterviewLink(candidate.interviewInvitationToken);
      window.open(interviewUrl, '_blank');
    }
  };

  const { data: pipeline } = useJobPipeline(job?.id ?? '');
  const showPipelineFooter = useMemo(
    () => (candidate ? hasCandidateFooterActions(candidate, pipeline?.stages) : false),
    [candidate, pipeline?.stages]
  );
  const getStageLabel = useStageDisplayLabel();
  const getStageTypeLabel = useStageTypeDisplayLabel(pipeline?.stages);
  const candidateStage = useMemo(
    () => (candidate ? findCandidateStage(candidate, pipeline?.stages) : undefined),
    [candidate, pipeline?.stages]
  );

  const {
    index: currentCandidateIndex,
    previousId: previousCandidateId,
    nextId: nextCandidateId,
  } = useMemo(
    () => getCandidateNeighbors(allCandidates, candidate?.id),
    [candidate?.id, allCandidates]
  );

  // Navigation functions
  const handlePreviousCandidate = () => {
    if (previousCandidateId) onNavigateToCandidate?.(previousCandidateId);
  };

  const handleNextCandidate = () => {
    if (nextCandidateId) onNavigateToCandidate?.(nextCandidateId);
  };

  const handleSaveEmail = async () => {
    if (!candidate || !emailInput.trim()) return;
    if (!isValidEmail(emailInput.trim())) {
      toast.error(t('notifications.invalid_email'));
      return;
    }
    try {
      await updateEmail({ candidateId: candidate.id, email: emailInput.trim() });
      toast.success(t('notifications.email_updated'));
      setIsEditingEmail(false);
      setEmailInput('');
      onStatusUpdate?.();
    } catch (err) {
      const fallback = t('notifications.email_update_failed');
      const errorMessage =
        err instanceof Error && err.message.trim() ? err.message : fallback;
      toast.error(errorMessage);
      console.error('Error updating email:', err);
    }
  };

  const handleCancelEmailEdit = () => {
    setIsEditingEmail(false);
    setEmailInput('');
  };

  // Check if navigation buttons should be enabled
  const canNavigatePrevious = previousCandidateId != null;
  const canNavigateNext = nextCandidateId != null;

  // Parse enhancedData using shared utility
  const parsedEnhancedData = parseEnhancedData(candidate?.enhancedData);

  const firstName = getEnhancedDataValue<string>(parsedEnhancedData, 'first_name');
  const lastName = getEnhancedDataValue<string>(parsedEnhancedData, 'last_name');
  const title = getEnhancedDataValue<string>(parsedEnhancedData, 'title');
  const matchScore = getEnhancedDataValue<number>(parsedEnhancedData, 'match_score');
  const metaCvHeaderScores = {
    matchScore:
      typeof matchScore === 'number'
        ? Math.round(matchScore)
        : typeof candidate?.score === 'number'
          ? Math.round(candidate.score / 100)
          : null,
    techScore:
      hasInterviewedStatus
        ? (
          assessment?.technical_assessment?.overall_tech_percent ??
          assessment?.technical_assessment?.overall_score ??
          null
        )
        : null,
    softScore:
      hasInterviewedStatus
        ? (
          assessment?.soft_skills_assessment?.overall_soft_skills_percent ??
          assessment?.soft_skills_assessment?.overall_score ??
          null
        )
        : null,
    fitScore: hasInterviewedStatus ? (assessment?.fit_score ?? assessment?.fit_assessment?.fit_score ?? null) : null,
  };
  const headerScoreCards = useMemo(() => {
    const cards = [
      {
        key: 'match',
        title: t('meta_cv.assessment.match_score_title', { defaultValue: 'Match score' }),
        subtitle: t('meta_cv.assessment.match_subtitle', { defaultValue: 'CV vs job description' }),
        value: metaCvHeaderScores.matchScore,
        isPrimary: false,
      },
    ];

    if (!hasInterviewedStatus) {
      return cards;
    }

    cards.push(
      {
        key: 'tech',
        title: t('meta_cv.assessment.tech_score_title', { defaultValue: 'Tech score' }),
        subtitle: t('meta_cv.assessment.tech_subtitle', { defaultValue: 'Technical assessment' }),
        value: metaCvHeaderScores.techScore,
        isPrimary: false,
      },
      {
        key: 'soft',
        title: t('meta_cv.assessment.soft_score_title', { defaultValue: 'Soft skills score' }),
        subtitle: t('meta_cv.assessment.soft_subtitle', { defaultValue: 'Soft skills AI test' }),
        value: metaCvHeaderScores.softScore,
        isPrimary: false,
      },
      {
        key: 'fit',
        title: t('meta_cv.assessment.fit_score_title', { defaultValue: 'Fit score' }),
        subtitle: t('meta_cv.assessment.fit_subtitle', { defaultValue: 'Combined score' }),
        value: metaCvHeaderScores.fitScore,
        isPrimary: true,
      }
    );

    return cards;
  }, [
    hasInterviewedStatus,
    metaCvHeaderScores.matchScore,
    metaCvHeaderScores.techScore,
    metaCvHeaderScores.softScore,
    metaCvHeaderScores.fitScore,
    t,
  ]);
  
  return (
    <>
      {/* Modal Container with Navigation and Backdrop */}
      {isOpen && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center px-3 py-6 sm:px-4 sm:py-8 md:px-6 md:py-0 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
            } transition-opacity duration-300 ease-in-out`}
        >
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            aria-hidden="true"
            onClick={onClose}
            {...getGTMAttributes(createGTMTracking('CLOSE_META_CV', { origin: 'meta_cv', target: 'close_backdrop' }))}
          />

          {/* Navigation Arrows — wrappers own absolute placement (Button base is `relative`;
              cn() does not twMerge, so absolute on className cannot override it). */}
          {allCandidates.length > 1 && (
            <>
              <div className="absolute left-6 top-1/2 z-[51] hidden -translate-y-1/2 lg:block">
                <Button
                  type="icon"
                  intent="light"
                  size="lg"
                  onClick={handlePreviousCandidate}
                  disabled={!canNavigatePrevious}
                  title={t('tooltips.previous_candidate')}
                  icon={<ChevronLeft/>}
                  className="shadow-xl hover:shadow-2xl"
                  {...getGTMAttributes(createGTMTracking('NAVIGATE_META_CV', { origin: 'meta_cv', target: 'previous' }))}
                />
              </div>

              <div className="absolute right-6 top-1/2 z-[51] hidden -translate-y-1/2 lg:block">
                <Button
                  type="icon"
                  intent="light"
                  size="lg"
                  onClick={handleNextCandidate}
                  disabled={!canNavigateNext}
                  title={t('tooltips.next_candidate')}
                  icon={<ChevronRight/>}
                  className="shadow-xl hover:shadow-2xl"
                  {...getGTMAttributes(createGTMTracking('NAVIGATE_META_CV', { origin: 'meta_cv', target: 'next' }))}
                />
              </div>

              <div className="absolute bottom-3 left-1/2 z-[51] hidden -translate-x-1/2 rounded-full bg-black bg-opacity-75 px-4 py-2 text-sm font-medium text-white shadow-lg lg:block">
                {t('meta_cv.navigation', {
                  current: currentCandidateIndex + 1,
                  total: allCandidates.length,
                })}
              </div>
            </>
          )}

          {/* Modal Container */}
          <div
            className={`bg-white shadow-2xl rounded-lg w-full max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-in-out max-w-[100vw] sm:max-w-3xl md:max-w-6xl ${isOpen ? 'scale-100' : 'scale-95'
            } ${className}`}
            data-tour="meta-cv-tour-panel"
            {...getGTMAttributes(createGTMTracking('META_CV_VISIBLE', { origin: 'meta_cv' }))}
          >

            {/* Header */}
            <div className="relative shrink-0 border-b border-gray-200 bg-white px-4 py-2 md:py-4 ">
              <div className="absolute right-4 top-2.5 md:right-6 md:top-4">
                <Button
                  type="icon"
                  intent="muted"
                  onClick={onClose}
                  title={t('tooltips.close')}
                  icon={<X/>}
                  data-tour="meta-cv-close"
                  {...getGTMAttributes(createGTMTracking('CLOSE_META_CV', { origin: 'meta_cv', target: 'close_x' }))}
                />
              </div>
              <div className="flex flex-col md:flex-row justify-between md:items-start md:pr-14">
                <div className="text-xs md:text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4>
                      {firstName && lastName ? `${firstName} ${lastName}` : candidate?.talentName}
                    </h4>
                    {candidate?.interviewInvitationToken &&
                      !isPendingStageSemantic(candidate.pipelineStageSemantic) && (
                        <Button
                          type="icon"
                          intent="muted"
                          onClick={handleOpenInterviewLink}
                          title={t('tooltips.open_interview_link')}
                          icon={<SquareArrowOutUpRight/>}
                          {...getGTMAttributes(createGTMTracking('OPEN_INTERVIEW_LINK_META_CV', { origin: 'meta_cv' }))}
                        />
                      )}
                    {(assessment?.has_cheating_flag) && (
                      <div className="hidden md:flex flex-wrap items-center gap-2">
                        {assessment?.has_cheating_flag && (
                          <SuspiciousActivityBadge size="lg"/>
                        )}
                      </div>
                    )}
                  </div>
                  {title && <p className="font-body text-dark-invirtus/80">{title}</p>}
                  {candidate?.hasValidEmail ? (
                    <p className="font-body text-dark-invirtus/80">{candidate?.talentEmail}</p>
                  ) : (
                    <div
                      className="mt-1"
                      onClick={(e) => e.stopPropagation()}
                      {...getGTMAttributes(createGTMTracking('UPDATE_EMAIL_META_CV', { origin: 'meta_cv', target: isEditingEmail ? 'email_field' : 'add' }))}
                    >
                      {isEditingEmail ? (
                        <EmailField
                          value={emailInput}
                          onChange={setEmailInput}
                          onSave={handleSaveEmail}
                          onCancel={handleCancelEmailEdit}
                          saving={isUpdatingEmail}
                          placeholder="candidate@example.com"
                        />
                      ) : (
                        <Button
                          type="text"
                          intent="danger"
                          size="sm"
                          onClick={() => setIsEditingEmail(true)}
                          label={t('meta_cv.add_email')}
                          icon={<AlertCircle />}
                        />
                      )}
                    </div>
                  )}

                </div>

                <div className="transition-[max-height,opacity] duration-300 ease-in-out md:max-h-none md:opacity-100 md:overflow-visible">
                  <div className="flex flex-row flex-wrap items-center justify-between gap-2 sm:flex-nowrap">
                    {(hasAssessment || assessment?.has_cheating_flag) && (
                      <div className="flex md:hidden flex-wrap items-center gap-2 order-first">
                        {assessment?.has_cheating_flag && (
                          <SuspiciousActivityBadge size="lg"/>
                        )}
                      </div>
                    )}

                    {/* Right-aligned group: score chip + download (same height, aligned on all sizes) */}
                    <div className="ml-auto flex items-center gap-2 shrink-0 order-last">
                      {!hasInterviewedStatus && (
                        hasAssessment && metaCvHeaderScores.fitScore !== null && metaCvHeaderScores.fitScore !== undefined ? (
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <span className="font-medium text-dark-invirtus/70 text-xs sm:text-sm whitespace-nowrap">{t('meta_cv.fit_score')}</span>
                            <Tag size="lg" intent={getScoreIntent(metaCvHeaderScores.fitScore)} variant="subtle">
                              {metaCvHeaderScores.fitScore}%
                            </Tag>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <span className="font-medium text-dark-invirtus/70 text-xs sm:text-sm whitespace-nowrap">{t('meta_cv.match')}</span>
                            <Tag
                              size="xl"
                              intent={getMatchScoreIntent(matchScore ?? (candidate?.score ? candidate.score / 100 : null))}
                              variant="subtle"
                            >
                              {matchScore ? `${matchScore}%` : candidate?.score ? `${candidate.score / 100}%` : t('meta_cv.not_available')}
                            </Tag>
                          </div>
                        )
                      )}

                      <div className="hidden sm:flex items-center gap-1.5">
                        <span className="font-medium text-dark-invirtus/70 text-xs sm:text-sm whitespace-nowrap">{t('meta_cv.status')}</span>
                        <StatusPill size="xl" variant={getCandidateStatusVariant(candidate?.pipelineStageSemantic || '')}>                          
                          {candidate?.pipelineStageSemantic
                            ? candidateStage
                              ? getStageLabel(candidateStage)
                              : getStageTypeLabel(candidate.pipelineStageSemantic)
                            : t('meta_cv.not_available')}
                        </StatusPill>
                      </div>

                      {job && candidate && (
                        <CandidateDownloadDropdown
                          candidate={candidate}
                          job={job}
                          canDownloadAssessment={hasAssessment}
                          origin="candidate_sidebar"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {hasInterviewedStatus && (
                <div className="mt-3.5 px-4.5">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4  sm:-translate-x-2.5 ">
                    {headerScoreCards.map((item) => {
                      const scoreValue = item.value !== null && item.value !== undefined
                        ? Math.max(0, Math.min(100, Math.round(item.value)))
                        : null;

                      return (
                        <div
                          key={item.key}
                          className={`w-full border border-gray-200 rounded-xl px-2.5 py-2 min-w-0 ${
                            item.isPrimary
                              ? 'bg-purple/5 border-t-4 border-t-purple'
                              : 'bg-white'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-base leading-[1.4] font-semibold font-heading text-[#2B2B2B]">
                              {item.title}
                            </p>
                            <p className="text-base leading-[1.4] font-semibold font-heading tracking-[-0.02em] text-[#0C0D0E]">
                              {scoreValue !== null ? `${scoreValue}%` : '--'}
                            </p>
                          </div>
                          <ProgressBar
                            value={scoreValue ?? 0}
                            max={100}
                            size="sm"
                            intent={item.isPrimary ? 'primary' : 'secondary'}
                            className="mt-2.5"
                            aria-label={item.title}
                          />
                          <p className="mt-2 text-sm leading-tight text-[#6D6D68]">
                            {item.subtitle}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 px-6 py-10 overflow-auto">
              {candidate ? (
                <CandidateInfo
                  candidate={candidate}
                  hasAssessment={hasAssessment}
                  assessment={assessment}
                  isLoadingAssessmentSummary={isLoadingAssessmentSummary}
                  selfieVideo={selfieVideo}
                  onAssessmentUpdated={setAssessment}
                />
              ) : (
                <div className="text-center text-gray-500 py-8 font-body">
                  {t('meta_cv.select_candidate')}
                </div>
              )}
            </div>

            {/* Footer with shared pipeline actions */}
            {candidate && job && showPipelineFooter && (
              <div className="shrink-0 px-6 py-4 border-t border-gray-200">
                <CandidateActions
                  candidate={candidate}
                  job={job}
                  examConfig={examConfig}
                  onStatusUpdate={() => {
                    onStatusUpdate?.();
                    onClose();
                  }}
                  requiresReview={requiresReview}
                  onDeleteCandidate={onDeleteCandidate}
                  onExtendInterview={onExtendInterview}
                  variant="footer"
                  origin="meta_cv"
                />
              </div>
            )}

            {allCandidates.length > 1 && (
              <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 lg:hidden">
                <Button
                  type="icon"
                  intent="light"
                  size="sm"
                  onClick={handlePreviousCandidate}
                  disabled={!canNavigatePrevious}
                  title={t('tooltips.previous_candidate')}
                  icon={<ChevronLeft/>}
                  {...getGTMAttributes(createGTMTracking('NAVIGATE_META_CV', { origin: 'meta_cv', target: 'previous' }))}
                />
                <Button
                  type="icon"
                  intent="light"
                  size="sm"
                  onClick={handleNextCandidate}
                  disabled={!canNavigateNext}
                  title={t('tooltips.next_candidate')}
                  icon={<ChevronRight/>}
                  {...getGTMAttributes(createGTMTracking('NAVIGATE_META_CV', { origin: 'meta_cv', target: 'next' }))}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CandidateSidebar;
